import type { IntegratorSubappKey } from "@hub-modules";

/** Primary nav items per integrated app shell — shared by `Layout` and `QuickSwitcher`. */
export interface NavItem {
  to: string;
  label: string;
  end?: boolean;
  /** Short blurb shown on the module home page card. Omitted entries are skipped on the home grid. */
  description?: string;
  /** Extra tokens for the quick launcher (abbreviations, alternate names). Not shown in the nav bar. */
  searchText?: string;
  /**
   * Other hub modules that co-home this URL (read-only included).
   * `navItemsForApp` and Layout chrome pick this up automatically: tabs appear on those
   * modules, and opening the page from one of them keeps that module's switcher.
   * Optionally also list the same `to` under those modules' `NAV_BY_APP` entries to control tab order.
   */
  coHomeAppKeys?: readonly IntegratorSubappKey[];
  /**
   * Module tab bar placement. Default `primary` (visible tabs, then overflow More).
   * `more` always lives under the More menu. `omit` stays in Go to… / home cards only.
   */
  navBar?: "primary" | "more" | "omit";
}

/** Co-homed GIPI/TSH list: MRP owns the route; accounting and manufacturing management may open it. */
export const PURCHASE_INVOICES_NAV_ITEM: NavItem = {
  to: "/mrp/purchase-invoices",
  label: "Purchase invoices",
  description:
    "Megaventory goods-in purchase invoices (GIPI) and tariff handling invoices (TSH).",
  searchText: "gipi tsh purchase invoice details goods in handling invoice",
  coHomeAppKeys: ["accounting", "manufacturing_management"],
};

/**
 * Scan-to-adjust unique carton labels. Not a module tab or home card.
 * Open from Go to…, a carton URL, or `/i/{cartonId}`. Recount is the usual entry.
 */
export const INVENTORY_CARTON_ADJUST_NAV_ITEM: NavItem = {
  to: "/warehouse/carton-adjust",
  label: "Carton adjust",
  description:
    "Scan a unique carton Data Matrix to recount quantity, split, combine, or reprint the 4x2 label.",
  searchText: "inventory box carton scan adjust license plate label recount",
  navBar: "omit",
};

/** SGS inventory discrepancy queue (kitting, carton adjust, shrinkage). Route under manufacturing management; warehouse co-home for approvers. */
export const INVENTORY_DISCREPANCY_NAV_ITEM: NavItem = {
  to: "/manufacturing-management/inventory-discrepancies",
  label: "Inventory discrepancies",
  description:
    "Review SGS inventory shrinkage and found-extra reports. Investigate transaction context, reply to submitters, and resolve with Megaventory adjustment or wastage.",
  searchText: "inventory discrepancy shrinkage wastage adjustment kitting sgs carton",
  coHomeAppKeys: ["warehouse"],
};

export function moduleNavBarItems(items: NavItem[]): NavItem[] {
  return items.filter((item) => navBarPlacement(item) !== "omit");
}

function isSettingsNavItem(item: NavItem): boolean {
  return item.label === "Settings" || item.to.endsWith("/settings");
}

/** Keep module Settings links after every other nav item (bar, home grid, quick switcher). */
export function sortNavItemsSettingsLast(items: NavItem[]): NavItem[] {
  const settings: NavItem[] = [];
  const rest: NavItem[] = [];
  for (const item of items) {
    if (isSettingsNavItem(item)) {
      settings.push(item);
    } else {
      rest.push(item);
    }
  }
  return [...rest, ...settings];
}

export function navBarPlacement(item: NavItem): NonNullable<NavItem["navBar"]> {
  return item.navBar ?? "primary";
}

export function moduleNavPrimaryItems(items: NavItem[]): NavItem[] {
  return items.filter((item) => navBarPlacement(item) === "primary");
}

export function moduleNavMoreItems(items: NavItem[]): NavItem[] {
  return items.filter((item) => navBarPlacement(item) === "more");
}

export const NAV_BY_APP: Record<IntegratorSubappKey, NavItem[]> = {
  erp: [
    {
      to: "/erp/mv-ingest",
      label: "Ingest",
      description:
        "Scheduled ingest for Megaventory, Freshsales, and other sources: cron, webhooks, run history, and per-entity knobs.",
      searchText: "mv ingest megaventory freshsales scheduler webhook shipping provider",
    },
    {
      to: "/erp/erpnext-sync",
      label: "ERPNext Sync",
      description: "Push synchronized ERP records into ERPNext and inspect recent sync runs.",
    },
    {
      to: "/erp/findings",
      label: "Findings",
      description:
        "Open ingest-event findings: drift, missing, orphan, and failed records flagged by sweeps and replays.",
    },
    {
      to: "/erp/data-control-registry",
      label: "Data control registry",
      description: "Registry of data-control rules and the records currently held under each rule.",
    },
    {
      to: "/erp/settings",
      label: "Settings",
      description: "Integrator-wide settings: API keys, scheduler tuning, and connector toggles.",
    },
  ],
  mrp: [
    { to: "/mrp/forecast", label: "Forecast" },
    {
      to: "/mrp/ghost-build-orders",
      label: "Ghost build orders",
      searchText: "build scenario what-if shortage ghost finished good",
    },
    {
      to: "/mrp/clamp-forecast",
      label: "Clamp forecast",
      searchText: "clamp fiber WYJ contract manufacturer monthly schedule",
    },
    { to: "/mrp/planning", label: "Planning" },
    {
      to: "/mrp/tla-planning",
      label: "TLA Planning",
      description:
        "TLA build-order supply versus open sales-order demand by site — same view as the Manufacturing Dashboard TLA status tab.",
      searchText:
        "tla top level assembly build order allocation sales order kodiak kodiak tla pcba prefix wireless fi-5a",
    },
    { to: "/mrp/build-plan", label: "Build plan" },
    { to: "/mrp/assemblies", label: "Assemblies" },
    { to: "/mrp/skus", label: "SKUs" },
    {
      to: "/mrp/overstock",
      label: "Overstock",
      searchText: "surplus months supply inventory excess",
    },
    {
      to: "/mrp/safety-stock-alerts",
      label: "Safety stock alerts",
      searchText: "reorder point rop reserve replenishment stock alert",
    },
    { to: "/mrp/aml", label: "AML" },
    { to: "/mrp/purchase-orders", label: "Purchase orders", searchText: "GPO purchase order" },
    PURCHASE_INVOICES_NAV_ITEM,
    { to: "/mrp/rfqs", label: "RFQ", searchText: "RFQ request for quote" },
    { to: "/mrp/suppliers", label: "Suppliers" },
    { to: "/mrp/incoming-parts", label: "Incoming parts" },
    { to: "/mrp/transmute", label: "Transmute" },
    { to: "/mrp/settings", label: "Settings" },
  ],
  bom: [
    {
      to: "/bom/browse",
      label: "BoM",
      description: "Browse bill-of-materials trees by SKU and revision.",
    },
    {
      to: "/bom/set-bom-rules",
      label: "Set BoM rules",
      description: "Edit BoM rule sets that drive automated SKU expansion.",
    },
    {
      to: "/bom/approve-bom-rules",
      label: "Approve BoM rules",
      description: "Review and approve pending BoM rule changes before they go live.",
    },
    {
      to: "/bom/push-megaventory",
      label: "Push to Megaventory",
      description: "Push approved BoM revisions into Megaventory and reconcile ProductBOM rows.",
    },
  ],
  admin: [
    {
      to: "/admin/hub-accounts",
      label: "Hub accounts",
      description: "Logins, hub roles, QuickBooks Time ids, and profile photos. Job facts and app access are in People.",
      searchText: "user users hub accounts roles login integrator user setting permissions",
    },
    {
      to: "/admin/contacts",
      label: "Contacts",
      description: "Org contact directory: hub-managed contacts plus Freshsales and Freshdesk search.",
      searchText: "contacts email freshdesk freshsales directory recipients",
    },
    {
      to: "/admin/tickets",
      label: "Tickets",
      description: "Hub support tickets opened by users and their resolution status.",
    },
    {
      to: "/admin/vendor-portal",
      label: "Vendor portal",
      description: "Invite vendor portal users, disable accounts, and assign supplier-specific vendor modules.",
    },
    {
      to: "/admin/server-access",
      label: "Server access",
      description: "Personal SSH accounts, permission tiers, self-service key links, and connection guides for Linux servers.",
      searchText: "ssh server access godaddy cloudlink keys authorized_keys sudoers deprovision",
    },
    {
      to: "/admin/module-access",
      label: "Module permissions",
      description:
        "Per-role and per-module integrator access: read, write, and module administrator tiers.",
      searchText: "permissions roles role rbac subapp module access matrix integrator user_role",
    },
    {
      to: "/admin/labor",
      label: "Labor",
      description: "Recent labor sessions for named shop-floor operators: devices scanned, start, and stop.",
    },
    {
      to: "/admin/time-card-changes",
      label: "Time card changes",
      description: "Review and apply requested QuickBooks Time card changes.",
      searchText: "time card qbt timesheet punch edit",
    },
    {
      to: "/admin/locations",
      label: "Locations",
      description: "Manage `public.location` rows and their Megaventory links.",
    },
    {
      to: "/admin/megaventory-payment-terms",
      label: "Payment term labels",
      description: "Display labels mapped to Megaventory payment-term enumerations.",
    },
    {
      to: "/admin/distributor-apis",
      label: "Distributor APIs",
      description: "Distributor API credentials and connector health.",
    },
    {
      to: "/admin/label-templates",
      label: "Label templates",
      description: "ZPL label templates: edit, preview as PNG, and send test prints.",
    },
    {
      to: "/admin/zpl-printers",
      label: "ZPL printers",
      description:
        "Published ZPL printer queues and label stock (4x2 or 4x6) for floor and shipping label printing.",
      searchText: "zebra printer queue relay zpl registry label stock 4x6 4x2 shipping",
    },
    {
      to: "/admin/product-scan-links",
      label: "Product scan links",
      description:
        "QR and data matrix scan URLs on product labels: map shipment and SKU to training pages and track adoption.",
      searchText: "qr data matrix scan link training adoption product label",
    },
    {
      to: "/admin/pdf-templates",
      label: "PDF templates",
      description: "pdf-it document templates: version, activate, and preview PDF output.",
    },
    {
      to: "/admin/settings",
      label: "Settings",
      description: "Admin module settings: support ticket topic owners and hub-wide admin configuration.",
    },
  ],
  clampqc: [
    {
      to: "/clamp-qc/inspection",
      label: "Inspection",
      description: "Pick a clamp build and record line-by-line inspection results on the job workspace.",
    },
    {
      to: "/clamp-qc/reporting",
      label: "Reporting",
      description: "Inspection roll-ups and per-line history across builds.",
    },
    {
      to: "/clamp-qc/settings",
      label: "Settings",
      description: "Clamp-QC defaults, station mappings, and reference data.",
    },
  ],
  manufacturing: [
    {
      to: "/manufacturing/kitting",
      label: "Picking",
      description: "Pull and assign serialized parts to active build orders on the shop floor.",
    },
    {
      to: "/manufacturing/configuration",
      label: "Configuration",
      description:
        "Configure and assemble SGS devices with SDTestApp: green-field builds plus inventory-alternate and QC-repair rebuilds.",
    },
    {
      to: "/manufacturing/laser-mark",
      label: "Laser mark",
      description:
        "Assign device serials from the pre-configuration queue and send a shared LightBurn template. Partial FI-3C and FI-5A fixture loads are allowed.",
      searchText: "lightburn laser mark fixture serial fi-3c fi-5a",
    },
    {
      to: "/manufacturing/benches",
      label: "Benches",
      description:
        "Registered SDTestApp bench PCs. Select a bench to observe remotely or confirm local loopback control on this workstation.",
      searchText: "sdtestapp bench machine registry relay observe",
    },
    {
      to: "/manufacturing/wireless-check",
      label: "Wireless check",
      description:
        "After Configuration, scan wireless device labels to confirm portal check-in and non-zero GPS since configure before Cal+Test.",
      searchText: "wireless portal check-in gps modem fi-5a-w dm-1",
    },
    {
      to: "/manufacturing/cal-test",
      label: "Cal+Test",
      description:
        "Run device calibration and production batch tests via SDTestApp on this bench PC. Cal+Test labor station.",
      searchText: "sdtestapp cal test production batch power pulse rack",
    },
    {
      to: "/manufacturing/sim",
      label: "SIM",
      description:
        "Look up ICCIDs in hub SIM inventory (carrier and activated status). Write access also supports Verizon activation and CSV upload.",
      searchText: "sim iccid lookup inventory carrier verizon wireless",
    },
    {
      to: "/manufacturing/assembly",
      label: "Assembly",
      description:
        "Open work orders at SGS whose finished good is not Made to order, including reorder-point builds and serialized assembly.",
    },
    {
      to: "/manufacturing/pre-encapsulation",
      label: "Pre-Encapsulation",
      description:
        "Scan device labels and clamps into the potting draft, add USA shell rows on the Unserialized tab, then supervisors create the Specialty Coating Systems potting purchase order on Potting PO.",
    },
    {
      to: "/manufacturing/encapsulation",
      label: "Encapsulation",
      description:
        "Clock in and out of potting or conformal coat labor. This station does not idle out, and other hub pages stay blocked while you are clocked in.",
      searchText: "encapsulation potting conformal coat clock in labor billable",
    },
    {
      to: "/manufacturing/final-qc",
      label: "Final QC",
      end: true,
      description: "Pass or fail finished devices and route failures to QC incidents.",
    },
    {
      to: "/manufacturing/qc-incidents",
      label: "QC incidents",
      description:
        "Open and resolve quality incidents, route devices to potting rework, and create rework purchase orders.",
    },
    {
      to: "/manufacturing/device-lookup",
      label: "Device lookup",
      description: "Trace a device label serial through batch, potting, QC, calibration, labor, and ship-out status.",
    },
  ],
  manufacturing_management: [
    {
      to: "/manufacturing-management/incoming-orders",
      label: "Incoming orders",
      description: "Unscheduled sales orders with open line quantity awaiting build assignment.",
    },
    {
      to: "/manufacturing-management/sales-order-details",
      label: "Sales order details",
      description: "Two-column browse of sales order header, ship-to, and line details.",
      searchText: "sales order details gso ship to customer lines",
    },
    {
      to: "/manufacturing-management/batch-details",
      label: "Batch details",
      description:
        "Browse SGS and Suntronic batches, open line-level progress, and jump to edit batch (same flow as Incoming orders Add to batch).",
      searchText: "batch list shipped assembled manufacturing dashboard",
    },
    {
      to: "/manufacturing-management/production-capacity",
      label: "Production capacity",
      description:
        "Dual-site forward schedule for SGS and Suntronic: TLA supply-by-date, staff labor budgets, and late risk by cause.",
      searchText: "capacity planner labor TLA schedule SGS SUN late",
    },
    {
      to: "/manufacturing-management/forward-demand",
      label: "Forward demand",
      description:
        "Unfulfilled sales order quantity by ship-by month and TLA (BoM topLevel), starting with the current month.",
      searchText: "unbatched unfulfilled ship by month planning TLA",
    },
    {
      to: "/manufacturing-management/cm-po",
      label: "CM PO",
      description: "Contract-manufacturer purchase orders, work-order defaults, and supplier site mappings.",
    },
    {
      to: "/manufacturing-management/work-orders",
      label: "Work orders",
      description:
        "Active and recent Megaventory build orders with material allocation, receive, BOM rebuild, and internal labor sync.",
      searchText: "build order allocate receive close rebuild bom gso",
    },
    {
      to: "/manufacturing-management/assembly-work-instructions",
      label: "Assembly work instructions",
      description:
        "Revisioned floor instructions for serialized assembly kinds. Submit and approve revisions before operators see them on Assembly work orders.",
      searchText: "assembly instructions steps approve revision baba",
    },
    {
      to: "/manufacturing-management/alternates",
      label: "Alternates",
      description: "Approved-alternate inventory board for production planning.",
    },
    {
      to: "/manufacturing-management/production-calendar",
      label: "Production calendar",
      description: "Planned builds across work centers, with capacity and conflict markers.",
    },
    {
      to: "/manufacturing-management/reporting",
      label: "Reporting",
      description: "Throughput, scrap, pending labor, and floor reports.",
    },
    {
      to: "/manufacturing-management/potting-po",
      label: "Potting PO",
      description:
        "Review open Specialty Coating Systems send cartons, print sealed carton labels, and create Megaventory potting purchase orders for first pass and rework. No labor clock-in on this page.",
      searchText: "potting purchase order carton label pre-encapsulation rework scs",
    },
    {
      to: "/manufacturing-management/box-label-generate",
      label: "Box label generate",
      description:
        "Print hub FI-3C, FI-5A, and FI-5A-W box labels for batch lines at SGS or Suntronic after device labels are scanned. Preview, test print, or print all.",
      searchText: "box label patrolman recon batch print zpl scanned serial",
    },
    {
      to: "/manufacturing-management/suntronic-box-labels",
      label: "Suntronic box labels",
      description:
        "List Suntronic hub-box batch lines and print box labels without device serials. Default quantity is 102% of the order, rounded up. Serials use year 1984 so they stay out of the live device-label namespace.",
      searchText: "suntronic box label print 1984 overage cm batch",
    },
    {
      to: "/manufacturing-management/clamp-qc-credit",
      label: "Clamp QC credit",
      description:
        "Scrapped clamp QC failures summarized by Megaventory supplier GPO with MV status; credit request workflow (create, submit, approve) is planned.",
      searchText: "reimbursement credit vendor clamp qc failed scrap",
    },
    INVENTORY_DISCREPANCY_NAV_ITEM,
    {
      to: "/manufacturing-management/safety-reports",
      label: "Safety reports",
      description:
        "Review safety concerns, near misses, and injury or incident reports. Assign owners, track root cause and corrective actions, and message reporters.",
      searchText:
        "safety report safety reports safety concern near miss injury incident hazard ppe first aid osha corrective action",
    },
    {
      to: "/manufacturing-management/stocking-orders",
      label: "Stocking orders",
      description:
        "Plan inventory builds and route completed stock between manufacturing sites without a customer sales order.",
      searchText: "stocking internal build suntronic transfer replenishment",
    },
    {
      to: "/manufacturing-management/transfers",
      label: "Transfers",
      description:
        "Plan pending transfer orders by combining open transfer requests from MRP. Post-pending pack/ship/receive stays in the sgs-intranet transfer app.",
      searchText: "transfer requests pending orders shipments plan workbench drag drop",
    },
    {
      to: "/manufacturing-management/wireless-assignments",
      label: "Wireless assignments",
      description:
        "Map wireless portal organizations to Freshsales / Megaventory customers and portal groups (Manufacturing-Operations parity).",
      searchText: "wireless portal organization group customer client assignment",
    },
    {
      to: "/manufacturing-management/wireless-issue",
      label: "Wireless issue",
      description:
        "Batch 2027 and 2062 PCBA cohort with shipment history, wireless portal read-replica check-ins, and operator flags for investigation units.",
      searchText: "wireless portal check-in IMEI PCBA flag GPO1708 batch 2027 2062",
    },
    PURCHASE_INVOICES_NAV_ITEM,
    {
      to: "/manufacturing-management/settings",
      label: "Settings",
      description: "Pre-Encapsulation allowlists, inventory discrepancy email distribution, and other manufacturing management configuration.",
    },
  ],
  documents: [
    {
      to: "/documents/workbench",
      label: "Workbench",
      description: "Search and edit controlled documents and their revisions.",
    },
    {
      to: "/documents/work-instructions",
      label: "Work instructions",
      description:
        "Create and edit work instructions. Revision 01 is the first version. Later saves record each changed field.",
      searchText: "work instruction editor document creator conformal coat revision",
    },
    {
      to: "/documents/collections",
      label: "Collections",
      description: "Product Collections: grouped documents shipped together as a controlled package.",
    },
    {
      to: "/documents/change-orders",
      label: "Engineering changes",
      description: "ECOs and per-contract-manufacturer ECNs (tracked in the hub; no outbound email).",
    },
    {
      to: "/documents/requests",
      label: "Request queue",
      description: "Staff requests for document access or missing controlled documents on a part number.",
      searchText:
        "document requests document request access request missing document request queue staff review",
    },
    {
      to: "/documents/drawing-coverage",
      label: "Drawing gaps",
      description:
        "Custom devices missing released 2D or 3D drawings, including coverage inherited from product collections.",
    },
    {
      to: "/documents/transitions",
      label: "Transition queue",
      description: "Pending revision transitions and notification fan-out status.",
    },
    {
      to: "/documents/settings",
      label: "Settings",
      description: "Document-control settings: types, inbound drawings email senders, families, notes, and admin tools.",
    },
  ],
  wiki: [{ to: "/wiki", label: "Wiki", end: true }],
  shipping: [
    {
      to: "/shipping/shipment-details",
      label: "Shipment details",
      description: "Read-only shipment context with batch and sales order cross-links.",
    },
    {
      to: "/shipping/outbound",
      label: "Outbound",
      description: "Outbound shipments: mark ready, capture carrier and tracking, and close the load.",
    },
    {
      to: "/shipping/one-off",
      label: "One-off label",
      description: "Create a UPS label for a package that is not a sales shipment or transfer.",
      searchText: "ups label address book one off package",
    },
    {
      to: "/shipping/pre-pack",
      label: "Pre-Pack",
      description: "Pre-pack queue and carton workflow that feeds outbound loads.",
    },
    {
      to: "/shipping/arrivals",
      label: "Arrivals",
      description: "Freight on the dock. Log cartons and print per-package arrived tags.",
      searchText: "inbound arrival dock freight package tag carton receiving",
    },
    {
      to: "/shipping/receive",
      label: "Receive",
      description: "First count, allocate to a purchase order or transfer, print tags, send to Holding or To stock.",
      searchText: "inbound receive first count purchase order transfer dock kiosk",
    },
    {
      to: "/shipping/holding",
      label: "Holding",
      description:
        "Inspect damaged, wrong SKU, packing slip mismatches, and IQC rule lines. A different person than Receive.",
      searchText: "inbound holding iqc damaged sku mismatch packing slip inspect cage recount",
    },
    {
      to: "/shipping/to-stock",
      label: "To stock",
      description: "Scan a clear inventory tag and put the carton away.",
      searchText: "inbound to stock put away shelf inventory tag",
    },
    {
      to: "/shipping/gosi-details",
      label: "GOSI details",
      description: "Megaventory sales invoice (GOSI) header, addresses, and line detail.",
      searchText: "GOSI GSO invoice sales invoice",
    },
    {
      to: "/shipping/paperwork",
      label: "Paperwork",
      description: "Upload and store packing slips and other shipping paperwork.",
    },
    {
      to: "/shipping/settings",
      label: "Settings",
      description: "Shipping module settings: inbound IQC rules, paperwork topic owners, and notification recipients.",
    },
  ],
  warehouse: [
    {
      to: "/warehouse/recount",
      label: "Recount",
      description:
        "Recount SKU on-hand at a warehouse location. Scan a carton to adjust quantity and Megaventory on-hand, or carve a new carton label from unclaimed bulk.",
      searchText: "recount inventory cycle count carton unclaimed bulk carve label",
    },
    {
      to: "/warehouse/location-label",
      label: "Location label",
      description:
        "Print a 4x2 SKU location tag for a warehouse site. Scanning the tag opens location adjust. Quantity is not printed, and on-hand does not change.",
      searchText: "location label zpl print sku site tag shelf",
      coHomeAppKeys: ["shipping", "manufacturing", "clampqc", "manufacturing_management"],
    },
    {
      to: "/warehouse/return-materials",
      label: "Return materials",
      description:
        "Scan a sheet that is coming back and enter how many labels are still on it.",
      searchText: "return materials sheet label bring back picked",
    },
    INVENTORY_CARTON_ADJUST_NAV_ITEM,
    INVENTORY_DISCREPANCY_NAV_ITEM,
    {
      to: "/warehouse/settings",
      label: "Settings",
      description: "Warehouse module settings.",
    },
  ],
  accounting: [
    {
      to: "/accounting/ap",
      label: "Vendor invoices",
      description:
        "Enter vendor invoices, match them to purchase orders and receipts, and review what is still open.",
      searchText: "accounts payable vendor invoice ap bill purchase order receipt",
    },
    {
      to: "/accounting/early-payment-requests",
      label: "Early payment requests",
      description:
        "Track requests to pay a vendor invoice promptly, including before the Megaventory GIPI is in.",
      searchText: "early payment vendor invoice pay before gipi overseas",
    },
    {
      to: "/accounting/cbp-monthly-statements",
      label: "Periodic monthly statements",
      description:
        "CBP duty grouped by release month, with the debit due date, the month total, and each related invoice status.",
      searchText: "cbp periodic monthly statement customs duty debit funds",
    },
    {
      to: "/accounting/suntronic-unbilled-labor",
      label: "Suntronic labor",
      description:
        "Suntronic finished-good service cost that is not fully matched to a manufacturer invoice, plus matched shipments.",
      searchText: "suntronic labor unbilled outstanding manufacturer invoice service cost wofg",
    },
    {
      to: "/accounting/suntronic-invoices",
      label: "Manufacturer invoices",
      description:
        "Match manufacturer invoices to finished goods and link Megaventory GIPI documents — currently Suntronic, with more manufacturers to follow.",
      searchText: "manufacturer invoices suntronic invoices mfr invoicing suntronic manufacturer invoice gipi",
    },
    PURCHASE_INVOICES_NAV_ITEM,
    {
      to: "/accounting/accounts-receivable",
      label: "Accounts receivable",
      description:
        "QuickBooks unpaid customer invoices and hub sales orders with quantity still to ship.",
      searchText: "accounts receivable ar unpaid invoices unshipped orders open balance collections",
    },
    {
      to: "/accounting/cash-flow",
      label: "Cash flow forecast",
      description: "Forecast cash from unpaid bills, receivables, and expected payments.",
      searchText: "cash flow forecast qbo aging bills receivables",
    },
    {
      to: "/accounting/inventory-over-time",
      label: "Inventory over time",
      description: "Chart inventory quantity and value over time.",
      searchText:
        "inventory history value quantity trend chart stock over time valuation",
    },
    {
      to: "/accounting/eom-reconciliation",
      label: "EOM reconciliation",
      description: "Month-end inventory and accounting reconciliation.",
      searchText: "end of month eom reconciliation inventory",
    },
    {
      to: "/accounting/reports",
      label: "Reports",
      description: "Accounting reports including uninvoiced open sales order backlog.",
      searchText: "uninvoiced open sales orders gosi fifo backlog ship by",
    },
    {
      to: "/accounting/receipt-discrepancy",
      label: "Receipt discrepancy",
      description:
        "Purchase orders where the vendor invoice, receipt, and work order quantities do not match, with every value in one place.",
      searchText:
        "receipt discrepancy discrepancies inbound holding short over damaged sku mismatch vendor invoice work order quantity compare purchase order office resolution",
    },
    {
      to: "/accounting/reimbursements",
      label: "Reimbursements",
      description: "Review employee expense, per diem, mileage, and wellness requests. Approve them or mark them reimbursed.",
      searchText: "employee reimbursement expense per diem mileage wellness receipt approve paid",
    },
    {
      to: "/accounting/settings",
      label: "Settings",
      description: "Accounting module settings: receipt discrepancy distribution list and notification recipients.",
    },
  ],
  quality: [
    {
      to: "/quality/qc-analysis",
      label: "QC Analysis",
      description:
        "After Receiving checks in a return, scan the carton tag or choose the RMA and log voltage, board revision, and visual inspection for each device.",
      searchText: "qc analysis station rma intake receiving carton scan board revision voltage",
    },
    {
      to: "/quality/rmas",
      label: "RMAs",
      description:
        "Return authorizations: issue RMA numbers, download the customer packing form, create UPS return labels, log intake, and link RCAs.",
      searchText: "rma return authorization intake ups label freshdesk rca packing form",
    },
    {
      to: "/quality/analysis-loans",
      label: "Analysis loans",
      description:
        "Units sent to TenX, a contract manufacturer, or another location for analysis. Recorded on RCA investigations and QC incidents. Print 4x2 loan labels from the send form.",
      searchText: "tenx analysis transfer loan rca investigation case",
    },
    {
      to: "/quality/engineering-changes",
      label: "Engineering changes",
      description:
        "Same ECO and ECN records as Document Control: authorize changes and track contract manufacturer implementation.",
      coHomeAppKeys: ["documents", "quality"],
    },
    {
      to: "/quality/cases",
      label: "Cases",
      description:
        "Quality event dossiers: investigations, linked QC incidents, and CAR snapshots for RMAs and batch findings.",
      searchText: "quality case investigation dossier rma ncr root cause",
    },
    {
      to: "/quality/rcas",
      label: "RCAs",
      description:
        "Root cause analyses. Open one to see the problem statement and the linked case, event, and NCRs.",
      searchText: "rca root cause analysis quality case ncr event",
    },
    {
      to: "/quality/events",
      label: "Events",
      description:
        "Inciting quality events and the NCRs linked to them. Open an event to see the source, summary, case, and unit records.",
      searchText: "quality event ncr nonconformity return complaint audit",
    },
    {
      to: "/quality/tier-1-review",
      label: "Tier 1 review",
      description: "Open NCRs that still need a tier 1 review.",
      searchText: "tier 1 review ncr script",
    },
    {
      to: "/quality/tier-2-review",
      label: "Tier 2 review",
      description: "Open NCRs escalated from a completed tier 1 review.",
      searchText: "tier 2 review ncr escalate loan",
    },
    {
      to: "/quality/ncrs",
      label: "NCRs",
      description:
        "Nonconformance records. Each row is one quality incident with its number, status, and disposition. Open a row for the unit, the resolution, and the notes.",
      searchText: "ncr nonconformance nonconformity quality incident disposition",
    },
    {
      to: "/quality/qc-incidents",
      label: "QC incidents",
      description:
        "Open and resolve quality incidents, route devices to potting rework, and create rework purchase orders.",
      searchText: "qc incident quality hold repair scrap potting rework",
    },
    {
      to: "/quality/corrective-actions",
      label: "Corrective actions",
      description:
        "ISO 9001 corrective action reports (CAR): problem description, containment, root cause, corrective action, verification, and closure.",
      searchText: "car capa nonconformity rma audit iso9001 iso 9001",
    },
    {
      to: "/quality/devices",
      label: "Device lookup",
      description:
        "Trace a device label serial through batch, potting, QC, calibration, labor, and ship-out status.",
      searchText: "serial fci episode device label imei pcba identity sales order shipment",
    },
    {
      to: "/quality/settings",
      label: "Settings",
      description: "Quality module settings, including the RMA receive quantity discrepancy distribution list.",
      searchText: "quality settings",
    },
  ],
  inside_sales: [
    {
      to: "/inside-sales/approvals",
      label: "Approvals",
      description: "Unified order and quote approval queue from processing through approved and sent.",
      searchText: "approval queue preapproval request changes AI draft order quote",
    },
    {
      to: "/inside-sales/wireless-portal-accounts",
      label: "Wireless portal",
      description:
        "Review wireless portal organizations and pending portal users when approved sales orders include wireless units.",
      searchText: "wireless portal account organization freshsales end user pending user",
    },
    {
      to: "/inside-sales/sales-orders",
      label: "Sales orders",
      description:
        "Review sales orders with line summaries, totals, and order status. Use New sales order on this page to create a Pending hub order.",
      searchText: "sales order overview gso pending approved attachments create new sales order",
    },
    {
      to: "/inside-sales/quotes",
      label: "Quotes",
      description: "Browse Hub-native inside-sales quotes, pricing, status, and PDFs.",
      searchText: "quote dashboard pricing pdf client end user",
    },
    {
      to: "/inside-sales/reports",
      label: "Reports",
      description: "Sort, filter, and search hub sales orders, including your Freshsales-owned end-user accounts.",
      searchText: "inside sales report sales order table filter sort search gso",
    },
    {
      to: "/inside-sales/quotes/new",
      label: "New quote",
      description: "Create a quote draft with server pricing and approval warnings.",
      searchText: "create quote new quote pricing lines",
      navBar: "omit",
    },
    {
      to: "/inside-sales/rep-agencies",
      label: "Rep agencies",
      description:
        "Commission rates, QuickBooks vendor ids, and Commissions.xlsx import for GOSI commission journal entry automation.",
      searchText: "rep agency commission rate qbo vendor comm rate spreadsheet",
      navBar: "more",
    },
    {
      to: "/inside-sales/customers",
      label: "Customers",
      description:
        "Browse Freshsales sales accounts and linked Megaventory client mirror fields. Read-only in Inside Sales.",
      searchText: "freshsales customer sales account client data end user",
      navBar: "more",
    },
    {
      to: "/inside-sales/new-product",
      label: "New product",
      description:
        "Add a new FI-3C, FI-5A, FI-5A-W, FP-UG, FP-OH, FP-OC, or ABB SKU — validate BoM parse and descriptions, create or restore Megaventory products, and push the BoM.",
      searchText: "new product sku fi-3c fi-5a fi-5a-w fp-ug fp-oh fp-oc abb megaventory bom create",
      navBar: "more",
    },
    {
      to: "/shipping/gosi-details",
      label: "GOSI details",
      description: "Megaventory sales invoice (GOSI) header, addresses, and line detail.",
      searchText: "GOSI GSO invoice sales invoice",
      navBar: "more",
    },
    {
      to: "/inside-sales/settings",
      label: "Settings",
      description:
        "Inside Sales approval distribution lists and sales-order defaults including payment terms and shipping.",
      searchText: "inside sales settings distribution processing doer approvers payment terms shipping",
      navBar: "more",
    },
  ],
  commissions: [
    {
      to: "/commissions/runs",
      label: "Monthly runs",
      description: "Compute, review, validate, and approve monthly commission runs.",
      searchText: "monthly run approve compute commission",
    },
    {
      to: "/commissions/rules",
      label: "Rules",
      description:
        "Sales date vs GOSI vs payment vs owing month, base commission, tier bonus, and new-end-user rules from the engine.",
      searchText: "commission rules timing sales date payment owed bonus tier",
    },
    {
      to: "/commissions/statements",
      label: "Statements",
      description: "Preview, send, and archive rep commission statements via Freshdesk.",
      searchText: "commission statement resend freshdesk",
    },
    {
      to: "/commissions/invoice-status",
      label: "Invoice status",
      description:
        "GOSI invoice commission workflow from customer payment through statement and rep vendor payment.",
      searchText: "invoice commission status workflow vendor payment statement",
    },
    {
      to: "/commissions/stocking",
      label: "Regional stocking",
      description: "Upload distributor POS reports and track remaining stocked inventory.",
      searchText: "pos report stocking tri-state regional",
    },
    {
      to: "/commissions/end-users",
      label: "End users",
      description: "New-end-user windows and per-end-user commission rate adjustments.",
      searchText: "end user bonus window adjustment eversource",
    },
    {
      to: "/commissions/rates",
      label: "Rates",
      description: "Rep agency base rates, tier rates, FCI volume bonus, and termination dates.",
      searchText: "commission rate tier bonus terminated",
    },
    {
      to: "/commissions/adjustments",
      label: "Adjustments",
      description: "Manual inputs, splits, corrections, and clawbacks with root-cause notes.",
      searchText: "manual adjustment correction clawback rca",
    },
    {
      to: "/commissions/reconciliation",
      label: "Reconciliation",
      description: "Monthly deposits vs commissions verification and actual payment entry.",
      searchText: "verification reconciliation deposits payments",
    },
  ],
  art_and_labels: [
    {
      to: "/art-and-labels/orders",
      label: "Label orders",
      description:
        "Vendor label purchase orders: preview print-house CSV, commit serials, void, and download per art file.",
      searchText: "label order purchase order csv preview void reprint vendor print house",
    },
    {
      to: "/art-and-labels/label-skus",
      label: "Label SKUs",
      description: "Map label SKUs to product SKUs and art files for the vendor print house.",
      searchText: "label sku art mapping product sku bar code",
    },
    {
      to: "/art-and-labels/art-files",
      label: "Art files",
      description: "Browse art file CSV rules and create label_info model records.",
      searchText: "art file rules label model barcode",
    },
  ],
  printing: [
    {
      to: "/printing/orders",
      label: "Label order generator",
      description:
        "Label purchase orders from Megaventory: preview printer CSV, commit serials, void, and download per art file.",
      searchText: "label order purchase order csv preview void reprint",
    },
    {
      to: "/printing/label-skus",
      label: "Label SKUs",
      description: "Map label SKUs to product SKUs and art files for external label printing.",
      searchText: "label sku art mapping product sku bar code",
    },
    {
      to: "/printing/art-files",
      label: "Art files",
      description: "Browse art file CSV rules and create label_info model records.",
      searchText: "art file rules label model barcode",
    },
  ],
  wireless: [
    {
      to: "/wireless/devices",
      label: "Devices",
      description:
        "Wireless portal devices with organization and group membership. Open a device for check-ins, events, and full device_information fields.",
      searchText: "wireless portal device imei serial organization group",
    },
    {
      to: "/wireless/organizations",
      label: "Organizations",
      description:
        "Portal customer organizations with tabs for groups, users, devices, and events, filterable by group.",
      searchText: "wireless portal organization customer domain group user device event",
    },
    {
      to: "/wireless/reports",
      label: "Reports",
      description:
        "Portal-wide check-in and event reports for a date range with CSV export, optionally filtered by device.",
      searchText: "wireless portal device records check-in event fault csv report",
    },
    {
      to: "/wireless/users",
      label: "Users",
      description: "List every wireless portal user and download the full directory as CSV.",
      searchText: "wireless portal users organization email phone role csv download",
    },
  ],
  marketing: [],
  engineering: [
    {
      to: "/engineering/firmware",
      label: "Firmware",
      description:
        "Browse firmware files and shared release notes, download build files, and advance versions through test, approval, production, and obsolete.",
      searchText: "firmware version file release notes hex zip approve beta production obsolete changelog",
    },
    {
      to: "/engineering/wireless-device-report",
      label: "Wireless device report",
      description:
        "Look up a wireless device by IMEI, ICCID, or hub serial. Pulls live production wireless check-ins and carrier status.",
      searchText: "wireless imei iccid carrier verizon vodafone att check-in portal device report",
    },
    {
      to: "/engineering/settings",
      label: "Settings",
      description: "Engineering notification distribution lists for firmware received and approved events.",
      searchText: "engineering settings firmware notifications distribution received approved",
    },
  ],
  people: [
    {
      to: "/people/directory",
      label: "Directory",
      description: "Staff list with title, department, manager, hire date, termination date, and open app count.",
      searchText: "staff directory employees hire date termination title manager people",
    },
    {
      to: "/people/org",
      label: "Org chart",
      description: "Reporting lines from each person's manager, with job title and hub roles.",
      searchText: "org chart organization chart manager supervisor reporting line",
    },
    {
      to: "/people/training",
      label: "Training",
      description: "Training matrix of people against active skills, with current and expired qualifications.",
      searchText: "training matrix skill qualification expired",
    },
    {
      to: "/people/skills",
      label: "Skills",
      description: "Maintain the skill catalog used as columns on the training matrix.",
      searchText: "skill catalog training matrix columns",
    },
    {
      to: "/people/permissions",
      label: "Hub permissions",
      description: "Read-only view of each person's hub roles and which modules those roles can open.",
      searchText: "permissions hub access roles modules who can",
    },
    {
      to: "/people/apps",
      label: "Apps",
      description: "Maintain the app catalog and which apps are on the standard onboarding list.",
      searchText: "onboarding offboarding apps logins access checklist standard",
    },
  ],
  personal: [
    {
      to: "/personal/home",
      label: "Personal dashboard",
      end: true,
      description:
        "Your personal dashboard of counts, short lists, charts, and favorite links. Save and share layout templates.",
      searchText: "home dashboard widgets templates favorites personal",
    },
    {
      to: "/personal/notifications",
      label: "Notifications",
      description:
        "In-app notifications for support tickets, purchase requests, transfer adjustments, and inventory discrepancies.",
      searchText: "notifications inbox unread snooze",
    },
    {
      to: "/personal/time-clock",
      label: "Time clock",
      description: "Clock in and out on QuickBooks Time; hub-local breaks and QBT lunch punches.",
      searchText: "quickbooks time qbt clock in out lunch break tsheets",
    },
    {
      to: "/personal/printing",
      label: "Print",
      description:
        "Send PDFs to shared CUPS printers, or print ABB PDD labels from the GoDaddy catch folder to a Zebra through the print relay.",
      searchText: "pdf print cups document office printer pdd zebra zpl abb label catch carton",
    },
    {
      to: "/personal/package-arrivals",
      label: "Package arrivals",
      description: "Track non-PO packages you ordered, with optional SKU and handoff notes.",
      searchText: "package arrival track tracking watch expected inbound handoff",
    },
    {
      to: "/personal/purchase-requests",
      label: "My purchase requests",
      description: "Request items for purchasing to order and track status with the MRP team.",
      searchText: "my purchase requests purchase request order buying",
    },
    {
      to: "/personal/reimbursements",
      label: "Reimbursements",
      description: "Submit expense, per diem, mileage, and wellness reimbursements with receipt photos.",
      searchText: "reimbursement expense per diem mileage wellness receipt camera",
    },
    {
      to: "/personal/document-requests",
      label: "My document requests",
      description: "Request controlled document access or a missing document on a part number.",
      searchText:
        "my document requests document requests document request access request missing document my requests submit",
    },
    {
      to: "/personal/dfm-change-review",
      label: "DFM suggestion",
      description: "Submit a design-for-manufacturing suggestion to the Asana DFM Change Review board.",
      searchText:
        "dfm suggestion manufacturing design suggestion design for manufacturing asana dcr change request",
    },
    {
      to: "/personal/safety-reports",
      label: "Safety report",
      description:
        "Report safety concerns, near misses, and safety incidents such as injuries, and follow up on your submissions.",
      searchText:
        "safety report safety concern near miss injury incident hazard ppe first aid osha",
    },
    {
      to: "/personal/tickets",
      label: "My tickets",
      description: "Bug reports and suggestions you submitted to the hub support team.",
      searchText: "support ticket bug suggestion my tickets",
    },
    {
      to: "/personal/chatgpt",
      label: "Hub MCP connector",
      description: "Connect ChatGPT or Claude to hub read tools matching your role, including the SGS playbook.",
      searchText: "chatgpt claude mcp connector oauth authorizations asana playbook",
    },
    {
      to: "/personal/settings",
      label: "Settings",
      description: "Account details and manufacturing tablet PIN setup.",
      searchText: "account settings tablet pin",
    },
  ],
};

/**
 * Append nav items that declare `coHomeAppKeys` for this module but are not already listed.
 * Listing the same `to` under the co-home module is optional and only needed to control tab order.
 */
export function appendCoHomedNavItems(
  app: IntegratorSubappKey,
  listed: readonly NavItem[],
  catalog: Record<string, readonly NavItem[]> = NAV_BY_APP,
): NavItem[] {
  const seen = new Set(listed.map((item) => item.to));
  const extras: NavItem[] = [];
  for (const [owner, items] of Object.entries(catalog)) {
    if (owner === app) {
      continue;
    }
    for (const item of items) {
      if (!item.coHomeAppKeys?.includes(app) || seen.has(item.to)) {
        continue;
      }
      extras.push(item);
      seen.add(item.to);
    }
  }
  return [...listed, ...extras];
}

export function navItemsForApp(app: IntegratorSubappKey | string): NavItem[] {
  if (!Object.hasOwn(NAV_BY_APP, app)) {
    return [];
  }
  const key = app as IntegratorSubappKey;
  return sortNavItemsSettingsLast(appendCoHomedNavItems(key, NAV_BY_APP[key]));
}
